import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IngestionBrandInfo,
  TriggerIngestionPayload,
  UpdateSchedulePayload,
  integrationApiService,
} from '../../services/integrationApi';
import { BrandTriggerDialog, BrandTriggerResult } from './BrandTriggerDialog';
import { ConnectorDetail, ConnectorPanel } from './ConnectorDetail';
import { ConnectorSidebar, listVisibleConnectors } from './ConnectorSidebar';
import { ScheduleDialog } from './ScheduleDialog';
import { ConnectorFilter, deriveConnectorHealth, needsAttention } from './connectorHealth';
import { ingest } from './ingestionTokens';

type PriceRefreshSource = 'trigger' | 'schedule' | 'header';

interface ConnectorWorkspaceProps {
  selectedBrandId: string | null;
  panel: ConnectorPanel;
  onSelectBrand: (brandId: string) => void;
  onPanelChange: (panel: ConnectorPanel) => void;
  keyboardEnabled?: boolean;
  hasRunningJob?: boolean;
  runningBrandName?: string;
}

export const ConnectorWorkspace: React.FC<ConnectorWorkspaceProps> = ({
  selectedBrandId,
  panel,
  onSelectBrand,
  onPanelChange,
  keyboardEnabled = true,
  hasRunningJob = false,
  runningBrandName,
}) => {
  const queryClient = useQueryClient();
  // Eight of ~22 connectors need attention on a normal day, so the console opens on those.
  const [filter, setFilter] = useState<ConnectorFilter>('attention');
  const [search, setSearch] = useState('');
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [dialogBrand, setDialogBrand] = useState<IngestionBrandInfo | null>(null);
  const [triggerResult, setTriggerResult] = useState<BrandTriggerResult | null>(null);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [triggerErrorJobId, setTriggerErrorJobId] = useState<string | null>(null);
  const [priceRefreshMessage, setPriceRefreshMessage] = useState<string | null>(null);
  const [priceRefreshError, setPriceRefreshError] = useState<string | null>(null);
  const [priceRefreshSource, setPriceRefreshSource] = useState<PriceRefreshSource | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  // Set when the trigger dialog was opened by a confirmed Force start (scrapers).
  const [forceTrigger, setForceTrigger] = useState(false);
  // The blocking run the admin confirmed (its start, ISO); the server refuses the force if another run replaced it.
  const [forceExpectedSince, setForceExpectedSince] = useState<string | null>(null);
  useEffect(() => {
    if (!triggerOpen) {
      setForceTrigger(false);
      setForceExpectedSince(null);
    }
  }, [triggerOpen]);

  const { data: brands, isLoading, isError } = useQuery({
    queryKey: ['ingestion-brands'],
    queryFn: () => integrationApiService.getIngestionBrands(),
    refetchInterval: 10000,
  });

  const visibleIds = useMemo(
    () => listVisibleConnectors(brands || [], filter, search, selectedBrandId).map((brand) => brand.id),
    [brands, filter, search, selectedBrandId]
  );

  const selectedBrand = (brands || []).find((brand) => brand.id === selectedBrandId) || null;
  const comingSoon = selectedBrand?.scraperStatus === 'coming_soon';
  const actionsDisabled =
    comingSoon || selectedBrand?.isRunning || hasRunningJob || false;
  // Force start closes the SELECTED connector's own running record: another brand's run is not
  // this connector's blocker, and the server only closes the brand it is asked to start.
  const blockedByRunning = !comingSoon && Boolean(selectedBrand?.isRunning || (selectedBrand?.stats?.running || 0) > 0);

  useEffect(() => {
    if (!brands || brands.length === 0) return;
    if (selectedBrandId && brands.some((brand) => brand.id === selectedBrandId)) return;
    // Open on a problem, not on whichever connector sorts first.
    const firstAttention = brands.find((brand) => needsAttention(deriveConnectorHealth(brand)));
    onSelectBrand((firstAttention || brands[0]).id);
  }, [brands, selectedBrandId, onSelectBrand]);

  const shortcutsEnabled = keyboardEnabled && !triggerOpen && !scheduleOpen;

  const apiConnectorMutation = useMutation({
    mutationFn: ({
      connectorId,
      force,
      expectedRunningSince,
    }: {
      connectorId: string;
      force?: boolean;
      expectedRunningSince?: string | null;
    }) => integrationApiService.runVendorConnector(connectorId, 'manual', { force, expectedRunningSince }),
    onSuccess: (data) => {
      setSnackbar(data?.message || 'API connector run queued');
      queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-ingestion-connectors'] });
    },
    onError: (error: { message?: string }) => {
      setSnackbar(error?.message || 'Failed to queue API connector run');
    },
  });

  useEffect(() => {
    if (!shortcutsEnabled) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      // These are bare-key shortcuts. Without this guard Cmd+R / Ctrl+R reaches
      // the `R` branch below, which calls preventDefault() and — for an API
      // connector — queues a run with no dialog. Reload must stay reload.
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (event.key === 'j' || event.key === 'J' || event.key === 'k' || event.key === 'K') {
        const currentIndex = selectedBrandId ? visibleIds.indexOf(selectedBrandId) : -1;
        if (currentIndex < 0) return;
        event.preventDefault();
        const delta = event.key.toLowerCase() === 'j' ? 1 : -1;
        const nextIndex = Math.max(0, Math.min(visibleIds.length - 1, currentIndex + delta));
        if (visibleIds[nextIndex]) onSelectBrand(visibleIds[nextIndex]);
      }
      if ((event.key === 'r' || event.key === 'R') && selectedBrand && !actionsDisabled) {
        event.preventDefault();
        if (selectedBrand.apiConnectorId) {
          apiConnectorMutation.mutate({ connectorId: selectedBrand.apiConnectorId });
          return;
        }
        setForceTrigger(false);
        setForceExpectedSince(null);
        setDialogBrand(selectedBrand);
        setTriggerError(null);
        setTriggerErrorJobId(null);
        setTriggerOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcutsEnabled, selectedBrand, selectedBrandId, visibleIds, onSelectBrand, actionsDisabled, apiConnectorMutation.mutate]);

  const triggerMutation = useMutation({
    mutationFn: ({ brandId, payload }: { brandId: string; payload: TriggerIngestionPayload }) =>
      integrationApiService.triggerIngestion(brandId, payload),
    onSuccess: (response) => {
      setTriggerError(null);
      setTriggerErrorJobId(null);
      if (response.executionMethod === 'local' && response.runCommand) {
        setTriggerResult({
          executionMethod: 'local',
          runCommand: response.runCommand,
          jobId: response.jobId,
          message: response.message,
        });
      } else {
        setTriggerOpen(false);
        setDialogBrand(null);
      }
      queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
    },
    onError: (error: { message?: string; data?: { runningJobId?: string } }) => {
      setTriggerResult(null);
      setTriggerError(error?.message || 'Failed to start ingestion job');
      setTriggerErrorJobId(error?.data?.runningJobId || null);
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: ({ brandId, payload }: { brandId: string; payload: UpdateSchedulePayload }) =>
      integrationApiService.updateBrandSchedule(brandId, payload),
    onSuccess: () => {
      setScheduleOpen(false);
      setDialogBrand(null);
      setPriceRefreshMessage(null);
      setPriceRefreshError(null);
      queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] });
    },
  });

  const priceRefreshMutation = useMutation({
    mutationFn: ({ brandId, region }: { brandId: string; region?: 'US' | 'CA'; source: PriceRefreshSource }) =>
      integrationApiService.triggerPriceRefresh(brandId, region),
    onSuccess: (data, variables) => {
      const message = data?.message || 'Price refresh started';
      if (variables.source === 'schedule') {
        setPriceRefreshError(null);
        setPriceRefreshMessage(message);
      } else if (variables.source === 'trigger') {
        setTriggerError(null);
        setTriggerErrorJobId(null);
        setTriggerOpen(false);
        setDialogBrand(null);
        setSnackbar(message);
      } else {
        setSnackbar(message);
      }
      queryClient.invalidateQueries({ queryKey: ['ingestion-brands'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['ingestion-jobs-running'] });
    },
    onError: (error: { message?: string; data?: { runningJobId?: string } }, variables) => {
      const message = error?.message || 'Failed to start price refresh';
      if (variables.source === 'schedule') {
        setPriceRefreshMessage(null);
        setPriceRefreshError(message);
      } else if (variables.source === 'trigger') {
        setTriggerResult(null);
        setTriggerError(message);
        setTriggerErrorJobId(error?.data?.runningJobId || null);
      } else {
        setSnackbar(message);
      }
    },
  });

  const handleTrigger = (brandId: string, payload: TriggerIngestionPayload) => {
    setTriggerError(null);
    setTriggerErrorJobId(null);
    // A confirmed force start goes through the brand trigger even for a price refresh: the
    // bulk price-refresh route skips a running brand, so it would start nothing.
    if (payload.type === 'price_refresh' && !forceTrigger) {
      setPriceRefreshSource('trigger');
      priceRefreshMutation.mutate({ brandId, region: payload.region, source: 'trigger' });
      return;
    }
    triggerMutation.mutate({
      brandId,
      payload: forceTrigger
        ? { ...payload, force: true, ...(forceExpectedSince ? { expectedRunningSince: forceExpectedSince } : {}) }
        : payload,
    });
  };

  /** Confirmed Force start: an API connector queues with force; a scraper opens its run options, then forces. */
  const handleForceStart = () => {
    if (!selectedBrand) return;
    if (selectedBrand.apiConnectorId) {
      apiConnectorMutation.mutate({
        connectorId: selectedBrand.apiConnectorId,
        force: true,
        expectedRunningSince: selectedBrand.runningSince ?? null,
      });
      return;
    }
    setForceTrigger(true);
    setForceExpectedSince(selectedBrand.runningSince ?? null);
    setDialogBrand(selectedBrand);
    setTriggerError(null);
    setTriggerErrorJobId(null);
    setTriggerOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // A failed background refetch must not blank a workspace that already has data,
  // or an operator loses the connector they were reading mid-triage.
  if (isError && (brands || []).length === 0) {
    return <Alert severity="error">Failed to load vendor connectors.</Alert>;
  }

  return (
    <Box
      sx={{
        // `minmax(0, 1fr)` on the detail track is load-bearing: without a zero
        // min-width the pane's content forces the grid wider and overflows the card.
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `${ingest.size.rail}px minmax(0, 1fr)` },
        gridTemplateRows: { xs: 'auto 1fr', md: '1fr' },
        height: { md: ingest.size.card },
        minHeight: { xs: 'calc(100vh - 260px)', md: ingest.size.card },
        border: `1px solid ${ingest.color.border}`,
        borderRadius: '10px',
        background: ingest.color.surface,
        boxShadow: ingest.shadow.card,
        overflow: 'hidden',
      }}
    >
      <ConnectorSidebar
        brands={brands || []}
        selectedBrandId={selectedBrandId}
        filter={filter}
        search={search}
        onFilterChange={setFilter}
        onSearchChange={setSearch}
        onSelect={onSelectBrand}
      />

      {selectedBrand ? (
        <ConnectorDetail
          brand={selectedBrand}
          panel={panel}
          expandedJobId={expandedJobId}
          onPanelChange={(next) => {
            setExpandedJobId(null);
            onPanelChange(next);
          }}
          onConfigure={() => {
            setDialogBrand(selectedBrand);
            setPriceRefreshMessage(null);
            setPriceRefreshError(null);
            setScheduleOpen(true);
          }}
          onRunNow={() => {
            if (selectedBrand.apiConnectorId) {
              apiConnectorMutation.mutate({ connectorId: selectedBrand.apiConnectorId });
              return;
            }
            setForceTrigger(false);
            setDialogBrand(selectedBrand);
            setTriggerError(null);
            setTriggerErrorJobId(null);
            setTriggerOpen(true);
          }}
          onForceStart={handleForceStart}
          forceStartAvailable={blockedByRunning && !triggerMutation.isPending && !apiConnectorMutation.isPending}
          forceStartPending={triggerMutation.isPending || apiConnectorMutation.isPending}
          onPriceRefresh={() => {
            setPriceRefreshSource('header');
            priceRefreshMutation.mutate({ brandId: selectedBrand.id, region: 'US', source: 'header' });
          }}
          onViewLog={(jobId) => {
            setExpandedJobId(jobId);
            onPanelChange('runs');
          }}
          runDisabled={actionsDisabled || triggerMutation.isPending || apiConnectorMutation.isPending}
          priceRefreshDisabled={actionsDisabled || priceRefreshMutation.isPending}
          priceRefreshPending={priceRefreshMutation.isPending && priceRefreshSource === 'header'}
          runningBrandName={runningBrandName}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            minWidth: 0,
          }}
        >
          <Typography sx={{ fontFamily: ingest.font.sans, fontSize: '12.5px', color: ingest.color.faintAlt }}>
            Select a connector to inspect its schedule, runs, and prices.
          </Typography>
        </Box>
      )}

      <BrandTriggerDialog
        open={triggerOpen}
        brand={dialogBrand}
        onClose={() => {
          setTriggerOpen(false);
          setDialogBrand(null);
          setForceTrigger(false);
          setTriggerError(null);
          setTriggerErrorJobId(null);
        }}
        onTrigger={handleTrigger}
        isPending={triggerMutation.isPending || (priceRefreshMutation.isPending && priceRefreshSource === 'trigger')}
        triggerResult={triggerResult}
        errorMessage={triggerError}
        errorJobId={triggerErrorJobId}
        onClearResult={() => setTriggerResult(null)}
      />

      <ScheduleDialog
        open={scheduleOpen}
        brand={dialogBrand}
        onClose={() => {
          setScheduleOpen(false);
          setDialogBrand(null);
          setPriceRefreshMessage(null);
          setPriceRefreshError(null);
        }}
        onSave={(brandId, payload) => scheduleMutation.mutate({ brandId, payload })}
        onRunPriceRefresh={(brandId, region) => {
          setPriceRefreshSource('schedule');
          setPriceRefreshError(null);
          priceRefreshMutation.mutate({ brandId, region, source: 'schedule' });
        }}
        isPending={scheduleMutation.isPending}
        isPriceRefreshPending={priceRefreshMutation.isPending && priceRefreshSource === 'schedule'}
        priceRefreshMessage={priceRefreshMessage}
        priceRefreshError={priceRefreshError}
        priceRefreshDisabled={dialogBrand?.scraperStatus === 'coming_soon' || hasRunningJob}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ConnectorWorkspace;
